// ===========================================
// Parent Event Detail Page
// ===========================================
// Displays a single published event with RSVP controls and (if paid)
// a Stripe Checkout button.  Holder name/email are prefilled from the
// authenticated user via useAuth().

import { useState, lazy, Suspense } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  CardMedia,
  Typography,
  Chip,
  Alert,
  CircularProgress,
  Button,
  Divider,
  Stack,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  FormHelperText,
  Grid,
  Paper,
} from '@mui/material';
import {
  Event as EventIcon,
  LocationOn as LocationIcon,
  ConfirmationNumber as TicketIcon,
  People as PeopleIcon,
  ArrowBack as BackIcon,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';
import PageHeader from '../../components/common/PageHeader';
import TicketModal from '../../components/events/TicketModal';
import ParentRsvpPanel from '../../components/events/ParentRsvpPanel';
import { useParentEvent, useTicketCheckout, useMyEventTickets } from '../../hooks/useEventsParent';
import { useAuth } from '../../contexts/useAuth';
import { useTenant, useSchoolTimezone } from '../../contexts/TenantContext';
import { formatInSchoolTz } from '../../utils/dateUtils';
import { formatEventWhen } from '../../components/events/eventCalendarItem';
import { titleCaseWords, countWord, NEUTRAL_CHECKOUT_WORDS } from '../../utils/checkoutWords';
import { bookingFeeTotalCents, paidTicketCount } from '../../utils/bookingFee';
import { capToSeats } from '../../utils/soldOut';
import type {
  BuyerBookingFee,
  CheckoutWords,
  EventType,
  ParentEventDetail,
  EventTicketTierSummary,
  TicketItemInput,
  MyEventTicket,
} from '../../api/events.api';
import AddToCalendarButtons from '../../components/events/AddToCalendarButtons';
import { EVENT_TYPE_LABELS } from '../../constants/eventTypes';
import { formatAddress } from '../../constants/countries';
import {
  TierQuantityPicker,
  tierPriceSummary,
  tierOrderTotalCents,
  quantitiesToItems,
  formatCents,
} from '../../components/events/EventTierPricing';

// Lazy-load the map so mapbox-gl stays out of the main bundle.
const VenueMap = lazy(() => import('../../components/events/VenueMap'));

// ===========================================
// HELPERS
// ===========================================

// ===========================================
// CHECKOUT PAYLOAD
// ===========================================

interface CheckoutPayload {
  items?: TicketItemInput[];
  quantity?: number;
  rsvpId?: string;
}

// ===========================================
// TICKET PURCHASE (paid events only)
// ===========================================

interface TicketPurchaseProps {
  ticketPriceCents: number | null;
  tiers: EventTicketTierSummary[];
  onCheckout: (payload: CheckoutPayload) => void;
  checkoutLoading: boolean;
  hasOwnedTickets: boolean;
  /** The server-resolved words for this event ("Buy" "tickets" / "Book" "places"). */
  words: CheckoutWords;
  /** The server-computed booking fee ({ mode, amountCents }; amountCents 0 = none). Display only: the server charges its own figure. */
  bookingFee: BuyerBookingFee;
  /** The school passes the card fee on, so Stripe adds it at the payment page (computed on the server). */
  cardFeeAtPayment: boolean;
  /** Ticket seats left (capacity less sold and held), from the server; null = no limit (B3b, #687). The family's own open checkout is not counted as taken. */
  seatsRemaining: number | null;
  /** None left only because OTHER families' open checkouts hold the rest (computed on the server, M2). Default OFF. */
  seatsAllHeld?: boolean;
  /** Admin preview: the panel shows as families see it, but nothing can be bought. Default OFF. */
  preview?: boolean;
}

function TicketPurchase({
  ticketPriceCents,
  tiers,
  onCheckout,
  checkoutLoading,
  hasOwnedTickets,
  words,
  bookingFee,
  cardFeeAtPayment,
  seatsRemaining,
  seatsAllHeld = false,
  preview = false,
}: TicketPurchaseProps) {
  const pluralTitle = titleCaseWords(words.plural);
  // Per-tier quantities (tier path). Legacy single-price path uses quantity.
  const [quantities, setQuantities] = useState<Record<string, number>>({});
  const [quantityChosen, setQuantity] = useState(1);
  // What the buyer may still pick: never more than the seats left.
  const legacyMax = capToSeats(8, seatsRemaining);
  const quantity = Math.max(1, Math.min(quantityChosen, legacyMax));

  const hasTiers = tiers.length > 0;
  const tierTotalQty = Object.values(quantities).reduce((s, q) => s + (q || 0), 0);
  const tierTotalCents = tierOrderTotalCents(tiers, quantities);
  // A fresh read can leave fewer seats than the cart holds: the button waits until the cart fits.
  const canBuyTiers = hasTiers && tierTotalQty >= 1 && tierTotalCents > 0 && (seatsRemaining == null || tierTotalQty <= seatsRemaining);

  const legacyTotalCents = (ticketPriceCents ?? 0) * quantity;

  // The booking fee is on top of the tickets: per PAID ticket (a $0 ticket type adds none) or once per booking when
  // the order holds a paid ticket. The card fee, if the school passes it on, is added at the payment page.
  const feeTicketCount = hasTiers ? paidTicketCount(tiers, quantities) : (ticketPriceCents ?? 0) > 0 ? quantity : 0;
  const feeCents = bookingFeeTotalCents(bookingFee, feeTicketCount);
  const payTierCents = tierTotalCents + feeCents;
  const payLegacyCents = legacyTotalCents + feeCents;

  const handleBuy = () => {
    if (preview) return;
    onCheckout(
      hasTiers ? { items: quantitiesToItems(quantities) } : { quantity }
    );
  };

  // Nothing left (B3b, #687). The family's OWN open checkout is never counted against them (the server leaves it out,
  // M2). When the rest are only in other families' open checkouts, it is not "sold out": they may free up shortly.
  if (seatsRemaining === 0 && seatsAllHeld) {
    return (
      <Alert severity="info" data-testid="seats-all-held">
        All the remaining {words.plural} are in other people&apos;s open checkouts right now. Any that are not paid for
        free up shortly, so please try again in a little while.
      </Alert>
    );
  }
  if (seatsRemaining === 0) {
    return (
      <Alert severity="info" data-testid="sold-out">
        Sold out. There are no {words.plural} left.
      </Alert>
    );
  }

  return (
    <Stack spacing={2}>
      {seatsRemaining != null && (
        <Typography variant="body2" color="text.secondary" data-testid="seats-left">
          {seatsRemaining} {countWord(words, seatsRemaining)} left
        </Typography>
      )}
      {hasTiers ? (
        <TierQuantityPicker tiers={tiers} quantities={quantities} onChange={setQuantities} words={words} seatsRemaining={seatsRemaining} />
      ) : (
        <FormControl fullWidth size="small">
          <InputLabel id="ticket-qty-label">{pluralTitle}</InputLabel>
          <Select
            labelId="ticket-qty-label"
            value={quantity}
            label={pluralTitle}
            onChange={(e) => setQuantity(Number(e.target.value))}
          >
            {[1, 2, 3, 4, 5, 6, 7, 8].filter((n) => n <= legacyMax).map((n) => (
              <MenuItem key={n} value={n}>
                {n}
              </MenuItem>
            ))}
          </Select>
          {ticketPriceCents != null && (
            <FormHelperText>Total: {formatCents(payLegacyCents)}</FormHelperText>
          )}
        </FormControl>
      )}

      {feeCents > 0 && (
        <Typography variant="body2" color="text.secondary" data-testid="booking-fee-line">
          Booking fee: {formatCents(feeCents)}{' '}
          {bookingFee.mode === 'PER_BOOKING'
            ? '(one fee for this booking)'
            : `(${formatCents(bookingFee.amountCents)} per paid ${words.singular})`}
          , included in the total
        </Typography>
      )}

      {cardFeeAtPayment && (
        <Typography variant="caption" color="text.secondary" data-testid="card-fee-caption">
          Plus a card processing fee at payment.
        </Typography>
      )}

      <Button
        variant={hasOwnedTickets ? 'outlined' : 'contained'}
        color="primary"
        startIcon={<TicketIcon />}
        onClick={handleBuy}
        disabled={preview || checkoutLoading || (hasTiers && !canBuyTiers)}
      >
        {checkoutLoading
          ? 'Redirecting…'
          : hasTiers
            ? `${hasOwnedTickets ? `${words.verb} more ${words.plural}` : `${words.verb} ${pluralTitle}`} — ${formatCents(payTierCents)}`
            : `${hasOwnedTickets ? `${words.verb} more ${words.plural}` : `${words.verb} ${titleCaseWords(countWord(words, quantity))}${quantity > 1 ? ` (${quantity})` : ''}`} — ${formatCents(payLegacyCents)}`}
      </Button>
    </Stack>
  );
}

// ===========================================
// RSVP (admin preview only)
// ===========================================

/**
 * What the RSVP panel shows in a preview: the shape of the form, with nothing to send and no read of any family's
 * answer. Past the deadline it says what a family that has not answered sees (`ParentRsvpPanel`, same test).
 */
function RsvpPreviewPanel({ registrationDeadline }: { registrationDeadline?: string | null }) {
  const closed = !!registrationDeadline && new Date(registrationDeadline).getTime() < Date.now();
  if (closed) {
    return (
      <Alert severity="info" data-testid="rsvp-preview">
        Registration for this event has closed.
      </Alert>
    );
  }
  return (
    <Stack spacing={2} data-testid="rsvp-preview">
      <Typography variant="body2" color="text.secondary">
        Families answer here: attending, number of guests and notes.
      </Typography>
      <Button variant="contained" color="primary" disabled>
        Submit RSVP
      </Button>
    </Stack>
  );
}

// ===========================================
// THE PAGE BODY (shared with the admin Preview)
// ===========================================

export interface ParentEventDetailViewProps {
  event: ParentEventDetail;
  ownedTickets: MyEventTicket[];
  ticketSuccess?: boolean;
  onCheckout?: (payload: CheckoutPayload) => void;
  checkoutLoading?: boolean;
  onBack?: () => void;
  /**
   * Admin preview: the same layout, with every action off (no buying, no RSVP, no calendar link, no
   * ticket-sign) and no read of any family's own data. Default OFF: only the preview page passes true.
   */
  preview?: boolean;
}

export function ParentEventDetailView({
  event,
  ownedTickets,
  ticketSuccess = false,
  onCheckout,
  checkoutLoading = false,
  onBack,
  preview = false,
}: ParentEventDetailViewProps) {
  const { schoolSlug } = useTenant();
  const schoolTimeZone = useSchoolTimezone();
  // Selected ticket for the "View ticket" QR display modal.
  const [selectedTicket, setSelectedTicket] = useState<MyEventTicket | null>(null);
  const handleCheckout = (payload: CheckoutPayload) => {
    if (preview) return;
    onCheckout?.(payload);
  };

  // The SCHOOL's wall clock, never the browser's: one helper for the when-line and the ticket label.
  const whenLine = formatEventWhen(event.startDateTime, event.endDateTime, schoolTimeZone);
  // "Sunday 06/12/2026, 7:00 pm": the same form as the ticket PDF.
  const ticketDateLabel = `${formatInSchoolTz(event.startDateTime, schoolTimeZone, {
    weekday: 'long',
  })} ${formatInSchoolTz(event.startDateTime, schoolTimeZone, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })}, ${formatInSchoolTz(event.startDateTime, schoolTimeZone, {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })}`;

  const venueLine = [event.venueName, event.Location?.name, event.Room?.name]
    .filter(Boolean)
    .join(', ');

  const typeLabel = EVENT_TYPE_LABELS[event.type as EventType] ?? event.type;
  // The server's words for THIS event; a page never decides the wording itself (events B2, #680).
  const words = event.checkoutWords ?? NEUTRAL_CHECKOUT_WORDS;

  return (
    <Box>
      <PageHeader
        title={event.title}
        breadcrumbs={
          // A preview sits in the admin area: no portal links.
          preview
            ? [{ label: event.title }]
            : [
                { label: 'Dashboard', path: `/${schoolSlug}/parent` },
                { label: 'Events', path: `/${schoolSlug}/parent/events` },
                { label: event.title },
              ]
        }
      />

      {ticketSuccess && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Payment successful — your {words.singular} has been issued. Check your email for confirmation.
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Main detail column */}
        <Grid item xs={12} md={7}>
          <Card elevation={0} sx={{ boxShadow: 'none' }}>
            {event.featuredImage && (
              <CardMedia
                component="img"
                height="240"
                image={event.featuredImage}
                alt={event.title}
                sx={{ objectFit: 'cover' }}
              />
            )}
            <CardContent>
              {/* Type + price chips */}
              <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                <Chip
                  label={typeLabel}
                  size="small"
                  sx={{ backgroundColor: '#4580E4', color: '#fff', fontWeight: 600 }}
                />
                {event.isFree ? (
                  <Chip
                    label="Free"
                    size="small"
                    sx={{ backgroundColor: '#96DAC9', color: '#fff', fontWeight: 600 }}
                  />
                ) : (
                  <Chip
                    label={
                      tierPriceSummary(event.EventTicketTier) ??
                      `$${((event.ticketPriceCents ?? 0) / 100).toFixed(2)}`
                    }
                    size="small"
                    icon={<TicketIcon sx={{ fontSize: '0.9rem !important' }} />}
                    sx={{ backgroundColor: '#FFCE00', color: '#080808', fontWeight: 600 }}
                  />
                )}
              </Stack>

              {/* Date/time */}
              <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mb: 1.5 }}>
                <EventIcon sx={{ color: 'primary.main', mt: 0.25, flexShrink: 0 }} />
                <Box>
                  <Typography variant="body1" fontWeight={600} data-testid="event-when">
                    {whenLine}
                  </Typography>
                </Box>
              </Stack>

              {/* Venue */}
              {venueLine && (
                <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mb: 1.5 }}>
                  <LocationIcon sx={{ color: 'primary.main', mt: 0.25, flexShrink: 0 }} />
                  <Typography variant="body1">{venueLine}</Typography>
                </Stack>
              )}

              {/* Venue map (only when a saved venue with coords is attached) */}
              {event.Venue && (
                <Box sx={{ mb: 2 }}>
                  <Suspense fallback={null}>
                    <VenueMap
                      latitude={event.Venue.latitude}
                      longitude={event.Venue.longitude}
                      name={event.Venue.name}
                      address={formatAddress(event.Venue)}
                    />
                  </Suspense>
                </Box>
              )}

              {/* School Location map (when the event uses a Location, not an
                  external Venue — one-shape rule means only one is ever set). */}
              {!event.Venue && event.Location && (
                <Box sx={{ mb: 2 }}>
                  <Suspense fallback={null}>
                    <VenueMap
                      latitude={event.Location.latitude}
                      longitude={event.Location.longitude}
                      name={event.Location.name}
                      address={formatAddress({ ...event.Location, country: event.Location.country ?? '' })}
                    />
                  </Suspense>
                </Box>
              )}

              {/* Add to calendar */}
              <AddToCalendarButtons
                eventId={event.id}
                title={event.title}
                startsAtIso={event.startDateTime}
                endsAtIso={event.endDateTime}
                details={event.description}
                location={venueLine || null}
                // The .ics route refuses anything not PUBLISHED, so offer nothing else. A preview of a DRAFT shows the
                // (disabled) buttons it will have once published; a closed event shows none, as families see it.
                available={event.status === 'PUBLISHED' || (preview && event.status === 'DRAFT')}
                disabled={preview}
              />

              {/* Capacity */}
              {event.capacity != null && (
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                  <PeopleIcon sx={{ color: 'text.secondary', flexShrink: 0 }} />
                  <Typography variant="body2" color="text.secondary">
                    Capacity: {event.capacity}
                    {event._count?.EventRSVP != null ? ` · ${event._count.EventRSVP} RSVPs` : ''}
                  </Typography>
                </Stack>
              )}

              {/* Description */}
              {event.description && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography
                    variant="body2"
                    color="text.primary"
                    sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.7 }}
                  >
                    {event.description}
                  </Typography>
                </>
              )}

              {/* Registration deadline */}
              {event.registrationDeadline && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="caption" color="text.secondary">
                    Registration deadline:{' '}
                    {formatInSchoolTz(event.registrationDeadline, schoolTimeZone, {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                      hour: 'numeric',
                      minute: '2-digit',
                      hour12: true,
                    })}
                  </Typography>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* RSVP / ticket sidebar */}
        <Grid item xs={12} md={5}>
          <Paper sx={{ p: 3, position: { md: 'sticky' }, top: { md: 16 } }}>
            <Typography variant="h6" gutterBottom>
              {event.isFree ? 'RSVP' : titleCaseWords(words.plural)}
            </Typography>
            <Divider sx={{ mb: 2 }} />

            {/* Owned-tickets panel — redirect-independent purchase confirmation.
                Shown for paid events when the parent already has PAID/CHECKED_IN
                tickets, regardless of whether the Stripe redirect returned. */}
            {!event.isFree && ownedTickets.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Alert
                  severity="success"
                  icon={<TicketIcon fontSize="inherit" />}
                  sx={{ mb: 1.5 }}
                >
                  You have {ownedTickets.length} {countWord(words, ownedTickets.length)} for this event.
                </Alert>
                <Stack spacing={1}>
                  {ownedTickets.map((ticket) => (
                    <Box
                      key={ticket.id}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1,
                        px: 1.5,
                        py: 1,
                        backgroundColor: 'background.paper',
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'divider',
                      }}
                    >
                      <Stack spacing={0.25}>
                        <Typography variant="body2" fontWeight={500}>
                          {ticket.EventTicketTier?.label ?? titleCaseWords(words.singular)}
                        </Typography>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography variant="body2" color="text.secondary">
                            {formatCents(ticket.priceCents)}
                          </Typography>
                          {ticket.status === 'CHECKED_IN' && (
                            <Chip
                              label="Checked in"
                              size="small"
                              icon={<CheckIcon />}
                              sx={{ backgroundColor: '#96DAC9', color: '#080808', fontWeight: 600 }}
                            />
                          )}
                        </Stack>
                      </Stack>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<TicketIcon />}
                        onClick={() => setSelectedTicket(ticket)}
                      >
                        View {words.singular}
                      </Button>
                    </Box>
                  ))}
                </Stack>
                <Divider sx={{ mt: 2, mb: 2 }} />
              </Box>
            )}

            {event.status === 'CANCELLED' ? (
              <Alert severity="error">This event has been cancelled.</Alert>
            ) : event.status === 'COMPLETED' ? (
              <Alert severity="info">This event has finished.</Alert>
            ) : event.isFree ? (
              preview ? (
                <RsvpPreviewPanel registrationDeadline={event.registrationDeadline} />
              ) : (
                <ParentRsvpPanel eventId={event.id} registrationDeadline={event.registrationDeadline} />
              )
            ) : (
              <TicketPurchase
                ticketPriceCents={event.ticketPriceCents}
                tiers={event.EventTicketTier ?? []}
                onCheckout={handleCheckout}
                checkoutLoading={checkoutLoading}
                hasOwnedTickets={ownedTickets.length > 0}
                words={words}
                bookingFee={event.bookingFee ?? { mode: 'PER_TICKET', amountCents: 0 }}
                cardFeeAtPayment={event.cardFeeAtPayment === true}
                seatsRemaining={event.seatsRemaining ?? null}
                seatsAllHeld={event.seatsAllHeld === true}
                preview={preview}
              />
            )}
          </Paper>
        </Grid>
      </Grid>

      {!preview && (
        <Button startIcon={<BackIcon />} onClick={onBack} sx={{ mt: 3 }}>
          Back to Events
        </Button>
      )}

      {selectedTicket && (
        <TicketModal
          open={!!selectedTicket}
          onClose={() => setSelectedTicket(null)}
          ticket={selectedTicket}
          words={words}
          eventTitle={event.title}
          eventDateLabel={ticketDateLabel}
          venueLine={venueLine}
        />
      )}
    </Box>
  );
}

// ===========================================
// MAIN PAGE
// ===========================================

export default function ParentEventDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { schoolSlug } = useTenant();

  const { data: queryData, isLoading, isError } = useParentEvent(id ?? '');
  const ticketCheckout = useTicketCheckout();
  const [searchParams] = useSearchParams();
  const ticketSuccess = searchParams.get('ticket') === 'success';

  // Fetch the parent's own tickets for this event (independent of the Stripe
  // redirect — works even when the ?ticket=success param is absent).
  // apiClient + the api method each strip one wrapper layer, so the hook
  // receives the bare MyEventTicket[].
  const { data: myTickets } = useMyEventTickets(id ?? '', !isLoading && !isError);
  const ownedTickets: MyEventTicket[] = myTickets ?? [];

  // apiClient + the api method each strip one wrapper layer, so the hook already
  // returns the bare ParentEventDetail (no further .data unwrap, no cast: the
  // compiler checks every field this page reads against what the server sends).
  const event = queryData;

  const holderName = user ? `${user.firstName} ${user.lastName}`.trim() : '';

  const handleCheckout = (payload: CheckoutPayload) => {
    if (!event) return;
    const base = window.location.origin;
    const successUrl = schoolSlug
      ? `${base}/${schoolSlug}/parent/events/${event.id}?ticket=success`
      : `${base}/parent/events/${event.id}?ticket=success`;
    const cancelUrl = window.location.href;

    ticketCheckout.mutate(
      {
        eventId: event.id,
        data: {
          holderName,
          // No holderEmail: the server buys under the caller's own login email (#708).
          ...(payload.items ? { items: payload.items } : { quantity: payload.quantity }),
          rsvpId: payload.rsvpId ?? null,
          successUrl,
          cancelUrl,
        },
      },
      {
        onSuccess: (result) => {
          // Hook already returns the bare TicketCheckoutResult.
          const url = (result as { url?: string })?.url;
          if (url) window.location.href = url;
        },
      }
    );
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isError || !event) {
    return (
      <Box>
        <PageHeader
          title="Event"
          breadcrumbs={[
            { label: 'Dashboard', path: `/${schoolSlug}/parent` },
            { label: 'Events', path: `/${schoolSlug}/parent/events` },
            { label: 'Event' },
          ]}
        />
        <Alert severity="error">Event not found or could not be loaded.</Alert>
        <Button
          startIcon={<BackIcon />}
          onClick={() => navigate(schoolSlug ? `/${schoolSlug}/parent/events` : '/parent/events')}
          sx={{ mt: 2 }}
        >
          Back to Events
        </Button>
      </Box>
    );
  }

  return (
    <ParentEventDetailView
      event={event}
      ownedTickets={ownedTickets}
      ticketSuccess={ticketSuccess}
      onCheckout={handleCheckout}
      checkoutLoading={ticketCheckout.isPending}
      onBack={() => navigate(-1)}
    />
  );
}
